import { AlertTriangle } from 'lucide-react'

import { Badge } from '@/components/ui'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/cn'

export interface CapabilityWarningBadgeProps {
  inconsistent?: boolean
  /** Sorted supports_* keys that disagreed across deployments. */
  conflictFlags?: string[]
  className?: string
  /** When true, also render a short list of flag names under the badge. */
  showFlagList?: boolean
}

function shortFlagName(flag: string): string {
  return flag.startsWith('supports_') ? flag.slice('supports_'.length) : flag
}

export function CapabilityWarningBadge({
  inconsistent,
  conflictFlags,
  className,
  showFlagList = false,
}: CapabilityWarningBadgeProps) {
  const { t } = useI18n()

  if (inconsistent !== true) return null

  const flags = (conflictFlags ?? []).filter(Boolean)
  const label = t('modelSelect.capabilityInconsistent')
  const baseTooltip = t('modelSelect.capabilityInconsistentTooltip')
  const flagsLine =
    flags.length > 0
      ? t('modelSelect.capabilityInconsistentFlags', { flags: flags.join(', ') })
      : t('modelSelect.capabilityInconsistentFlagsUnknown')
  const tooltip = `${baseTooltip} ${flagsLine}`

  return (
    <span className={cn('inline-flex max-w-full flex-col items-start gap-0.5', className)}>
      <Badge
        variant="warning"
        size="sm"
        className="shrink-0 px-1.5 py-0 text-[10px] leading-4"
        title={tooltip}
        aria-label={`${label}: ${tooltip}`}
      >
        <AlertTriangle className="h-3 w-3" aria-hidden />
        {label}
      </Badge>
      {showFlagList && flags.length > 0 && (
        <span
          data-testid="capability-conflict-flags"
          className="max-w-[18rem] font-sans text-[10px] leading-snug text-amber-800/90"
          title={flags.join(', ')}
        >
          {flags.map(shortFlagName).join(' · ')}
        </span>
      )}
    </span>
  )
}
